import React, { useState, useEffect, useMemo } from 'react';
import './style.scss';
import { EscalatorItem } from './EscalatorItem';
import { EscalatorData } from './type';
import { canPlayAudio } from './helpers';
import { getCubbyAssets } from './schedule';
import { useEscalatorSchedule } from './useEscalatorSchedule';
import { EscalatorPreviewOverlay } from './EscalatorPreviewOverlay';
import { EscalatorPreviewBackground } from './EscalatorPreviewBackground';

export default function Escalator({ data, muted: defaultMuted = true }: { data: EscalatorData, muted?: boolean }) {
  const urlParams = new URLSearchParams(window.location.search);
  const isEditor = urlParams.get('display') === 'editor';
  const chapterParam = urlParams.get('chapter');
  const previewChapterIndex = ['', null, undefined].includes(chapterParam)
    ? null
    : Number.parseInt(chapterParam as string, 10);
  const pinnedChapterIndex = Number.isNaN(previewChapterIndex) ? null : previewChapterIndex;

  const [muted, setMuted] = useState<boolean | null>(null);

  const chapters = data?.chapters ?? [];
  const activeChapter = useEscalatorSchedule(chapters, {
    enabled: chapters.length > 0,
    previewChapterIndex: pinnedChapterIndex,
  });

  useEffect(() => {
    canPlayAudio().then(() => {
      const m = urlParams.get('muted');
      if (m !== null) {
        setMuted(m === 'false' ? false : !!m);
      } else {
        setMuted(defaultMuted);
      }
    }).catch(() => {
      setMuted(true);
    });
  }, [defaultMuted]);

  const cubbyAssets = useMemo(() => {
    if (!activeChapter || activeChapter.chapter.chapter_type === 'delay') {
      return [];
    }
    return getCubbyAssets(activeChapter.chapter.assets);
  }, [activeChapter]);

  if (muted === null) {
    return null;
  }

  const containerClassName = `escalator-container${isEditor ? ' is-editor' : ''}`;
  const previewOverlay = isEditor ? (
    <EscalatorPreviewOverlay chapters={chapters} pinnedChapterIndex={pinnedChapterIndex} />
  ) : null;

  const cubbyParam = urlParams.get('cubby');
  if (cubbyParam && !isEditor) {
    const cubbyData = cubbyAssets[parseInt(cubbyParam, 10) - 1];
    if (cubbyData) {
      return (
        <div className={containerClassName}>
          {previewOverlay}
          <EscalatorItem
            url={cubbyData.url}
            loop={true}
            muted={muted}
            width="100vw"
            height="100vh"
          />
        </div>
      );
    }
  }

  const previewPreset = activeChapter?.chapter.preset ?? 1;

  return (
    <div className={containerClassName}>
      {previewOverlay}
      <div className="escalator-canvas">
        {isEditor ? (
          <EscalatorPreviewBackground preset={previewPreset} muted={muted} />
        ) : null}
        {cubbyAssets.map((asset, idx) => (
          <div key={`cubby-${idx}`} className={`escalator-cubby escalator-cubby--${idx}`}>
            <EscalatorItem
              url={asset.url}
              loop={true}
              muted={muted}
              width={56}
              height={56}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
